const placeIdKey = place => {
  const id = place?.id;
  return id === null || id === undefined || id === "" ? "" : `id:${String(id)}`;
};

const normalizeIdentityPart = value => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, " ")
  .trim();

const placeIdentityKey = place => {
  const name = normalizeIdentityPart(place?.name);
  const location = normalizeIdentityPart(place?.location);
  return name && location ? `place:${name}|${location}` : "";
};

const placeCategoryText = place => [
  place?.tipo,
  place?.primaryCategory,
  place?.primary_category,
  ...(Array.isArray(place?.categories) ? place.categories : []),
].filter(Boolean).join(" · ");

const CATEGORY_SUBTITLES = {
  Tacos: "Árabes · birria · asada",
  Burgers: "Hamburguesas · sirloin · gourmet",
  Mexicana: "Cemitas · antojitos · casera",
  Desayunos: "Chilaquiles · brunch · desayuno",
  Pizza: "Pizza · italiana · horno",
  Mariscos: "Ceviche · aguachile · pescado",
  Carnes: "Parrilla · asador · BBQ",
  "Ramen & Japonesa": "Ramen · sushi · japonesa",
  "Café & Postres": "Café · postres · helados",
  "Pub & Bar": "Pub · bar · cervezas",
  Buffet: "Buffet · cortes · variedad",
  "Otros del catálogo": "Especialidades · variedad · locales",
};

const subtitleFor = label => CATEGORY_SUBTITLES[label] || "Sabores · opciones · lugares";

const uniquePlaces = places => {
  const seenIds = new Set();
  const seenIdentities = new Set();
  return (Array.isArray(places) ? places : []).filter(place => {
    const idKey = placeIdKey(place);
    const identityKey = placeIdentityKey(place);
    if ((idKey && seenIds.has(idKey)) || (identityKey && seenIdentities.has(identityKey))) return false;
    if (idKey) seenIds.add(idKey);
    if (identityKey) seenIdentities.add(identityKey);
    return !!(idKey || identityKey);
  });
};

export const shouldUseCatalogRows = ({ smartQuery = "", searchQuery = "" } = {}) => (
  !String(smartQuery || "").trim() && !String(searchQuery || "").trim()
);

export const buildCatalogRows = ({ places = [], categories = [], activeFilter = "Todos" } = {}) => {
  const catalog = uniquePlaces(places);
  const visualCategories = (Array.isArray(categories) ? categories : []).filter(category => category?.label && category.label !== "Todos" && typeof category.match === "function");

  if (activeFilter !== "Todos") {
    const category = visualCategories.find(item => item.label === activeFilter);
    if (!category) return [];
    const matches = catalog.filter(place => category.match(placeCategoryText(place)));
    return matches.length ? [{ label: category.label, subtitle: subtitleFor(category.label), relation: "category", places: matches }] : [];
  }

  const used = new Set();
  const rows = [];

  visualCategories.forEach(category => {
    const matches = catalog.filter(place => {
      const key = placeIdKey(place) || placeIdentityKey(place);
      return !used.has(key) && category.match(placeCategoryText(place));
    });
    if (!matches.length) return;
    matches.forEach(place => used.add(placeIdKey(place) || placeIdentityKey(place)));
    rows.push({ label: category.label, subtitle: subtitleFor(category.label), relation: "category", places: matches });
  });

  // A multi-category place is still rendered once in the "Todos" overview to avoid
  // duplicate cards, but filtering by any of its stored categories will find it.
  const uncategorized = catalog.filter(place => !used.has(placeIdKey(place) || placeIdentityKey(place)));
  if (uncategorized.length) rows.push({ label: "Otros del catálogo", subtitle: subtitleFor("Otros del catálogo"), relation: "uncategorized", places: uncategorized });

  return rows;
};